import { useCallback, useState, useMemo } from 'react';
import {
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { useStoreContext } from 'store';
import { toggleIsSideNavOpen } from 'store/actions';
import { menuLinks } from 'routes/links';
import { LocalStorage, LocalStorageEnum } from 'helpers/localStorage';
import { DRAWER_WIDTH } from 'theme/constants';
import NavLinks from './NavLinks';
import Logo from '../../Logo';
import { DrawerHeader, Drawer } from './styled';
import { activeLinkItemMixin } from './mixins';

const SideNav = () => {
  const {
    state: { isSideNavOpen },
    dispatch,
  } = useStoreContext();

  const [isHovering, setIsHovering] = useState(false);
  const computedOpen = useMemo(
    () => isSideNavOpen || isHovering,
    [isSideNavOpen, isHovering],
  );
  const isOverlay = useMemo(
    () => !isSideNavOpen && isHovering,
    [isSideNavOpen, isHovering],
  );

  const handleMouseEnter = useCallback(() => {
    if (!isSideNavOpen) setIsHovering(true);
  }, [isSideNavOpen]);

  const handleMouseLeave = useCallback(() => {
    if (!isSideNavOpen) setIsHovering(false);
  }, [isSideNavOpen]);

  const toggleSideNav = useCallback(() => {
    dispatch(toggleIsSideNavOpen());
    LocalStorage.set(
      LocalStorageEnum.IS_SIDE_NAV_OPEN,
      (!isSideNavOpen).toString(),
    );
  }, [dispatch, isSideNavOpen]);

  return (
    <Drawer
      variant="permanent"
      open={isSideNavOpen}
      sx={isOverlay ? { overflow: 'visible', position: 'relative' } : undefined}
      PaperProps={{
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        sx: (theme) => ({
          background: theme.palette.background.paper,
          ...(isOverlay && {
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${DRAWER_WIDTH}px !important`,
            boxShadow: theme.shadows[8],
            zIndex: theme.zIndex.drawer + 1,
          }),
        }),
      }}
    >
      <DrawerHeader>
        <Logo />
      </DrawerHeader>
      <List sx={{ overflow: 'hidden' }} disablePadding>
        <Divider sx={{ mb: 0.5 }} />
        <NavLinks links={menuLinks.mainMenuLinks} isOpen={computedOpen} />
        <Divider sx={{ my: 0.5 }} />
        <NavLinks links={menuLinks.adminMenuLinks} isOpen={computedOpen} />
      </List>
      <Divider sx={{ mt: 'auto', mb: 0 }} />
      <ListItem disablePadding>
        <ListItemButton
          onClick={toggleSideNav}
          sx={(theme) => ({
            ...activeLinkItemMixin(theme),
            py: 1,
          })}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: computedOpen ? 3 : 'auto',
              justifyContent: 'center',
            }}
          >
            <MenuOpenIcon />
          </ListItemIcon>
          <ListItemText
            primary={isSideNavOpen ? 'Minify menu' : 'Fix menu'}
            sx={{ opacity: computedOpen ? 1 : 0 }}
          />
        </ListItemButton>
      </ListItem>
    </Drawer>
  );
};

export default SideNav;
